import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import AboutPage from './about';
import { assets, campaign, researchNotes } from './campaign';
import { SmsConsentDisclosure } from './sms-disclosure';
import ContactPage from './contact';
import ElectionInformationPage from './election-information';
import EndorsementsPage from './endorsements';
import PrivacyPolicyPage from './privacy-policy';
import TermsAndConditionsPage from './terms-and-conditions';

const navItems = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  { label: 'Election Info', to: '/election-information' },
  { label: 'Endorsements', to: '/endorsements' },
  { label: 'Contact', to: '/contact' },
];

const priorities = [
  {
    title: 'Lower taxes & cost of living',
    body: 'The cost of owning a home or business in Galveston should not come with rising annual costs. The City of Galveston can live within its means and provide quality services with a focus on improving infrastructure, investing in public safety, and planning for the future. Beau Rawlins has been fighting for fiscally responsible government and finding wasteful spending that can go toward improving quality of life for residents.',
  },
  {
    title: 'Improve public safety',
    body: "Galveston is unique, with millions of visitors every year and real challenges for local first responders in police, fire, and EMS. The city must continue investing in public safety so residents can feel confident in their families' safety and enjoy everything Galveston has to offer. Beau has been a staunch supporter of first responders and is endorsed by the Galveston Municipal Police Association and Galveston Firefighters Association.",
  },
  {
    title: 'Better roads & drainage',
    body: 'Residents deserve a city government focused on improving infrastructure with new roads and better drainage. By reining in unnecessary spending and leveraging available state and federal dollars, Galveston can reinvest in the infrastructure residents rely on. Beau is focused on improving roads and drainage and committed to driving council toward real results.',
  },
];

const proofPoints = [
  'Runoff Election Day is Saturday, June 13',
  'Polls are open 7:00 AM to 7:00 PM',
  'Current Galveston City Council Member for District 5',
  'Endorsed by Galveston police and firefighters',
];

function App() {
  return (
    <div
      className="min-h-screen bg-[#f8f1fb] text-[#281331]"
      style={{ fontFamily: 'Montserrat, sans-serif' }}
    >
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&family=Source+Serif+4:wght@500;700;900&display=swap');`}</style>
      <Header />
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/election-information" element={<ElectionInformationPage />} />
        <Route path="/endorsements" element={<EndorsementsPage />} />
        <Route path="/events" element={<EndorsementsPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/donate" element={<Navigate to="/contact" replace />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
        <Route path="/blank" element={<PrivacyPolicyPage />} />
        <Route path="/blank-1" element={<TermsAndConditionsPage />} />
      </Routes>
      <Footer />
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);

  return null;
}

function PalmStars({ className = '' }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 220 150"
      role="img"
      aria-label="Gold palm tree with three white stars"
    >
      <g fill="#f5c400">
        <path d="M141 122c8-34 8-62 1-84l15-3c7 26 6 56-1 90l-15-3Z" />
        <path d="M158 36c10-31 32-38 53-26-25 8-38 18-43 39l-10-13Z" />
        <path d="M151 46c21-22 45-21 62-4-28-4-43 2-55 18l-7-14Z" />
        <path d="M159 48c2-25-13-42-36-45 16 14 22 28 20 45h16Z" />
        <path d="M151 50c-16-20-35-19-49-1 24-4 37 2 46 17l3-16Z" />
        <path d="M148 63c-18-7-33 2-41 20 18-12 31-14 44-5l-3-15Z" />
        <path d="M153 63c24-5 41 7 46 30-18-17-35-22-52-12l6-18Z" />
      </g>
      <g fill="#fff">
        <polygon points="42 88 49 109 71 109 53 122 60 143 42 130 24 143 31 122 13 109 35 109" />
        <polygon points="110 77 117 100 141 100 122 113 129 136 110 122 91 136 98 113 79 100 103 100" />
        <polygon points="178 88 185 109 207 109 189 122 196 143 178 130 160 143 167 122 149 109 171 109" />
      </g>
    </svg>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      <header className="sticky top-0 z-50 border-b-4 border-[#f2b800] bg-[#6f2182] text-white shadow-2xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between p-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="flex items-center gap-3 leading-none"
            onClick={() => setOpen(false)}
          >
            <PalmStars className="h-11 w-16 shrink-0" />
            <span>
              <span className="block text-2xl font-black uppercase tracking-[0.18em]">
                Beau Rawlins
              </span>
              <span className="block text-xs font-bold uppercase tracking-[0.24em] text-[#f5c400]">
                Galveston District 5
              </span>
            </span>
          </Link>

          <nav className="hidden lg:block">
            <ul className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em]">
              {navItems.map((item) => (
                <li key={item.to}>
                  <Link
                    className={`rounded-full px-4 py-2 transition ${isActive(item.to) ? 'bg-white text-[#6f2182]' : 'text-white hover:bg-white/15 hover:text-[#f5c400]'}`}
                    to={item.to}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <button
            type="button"
            className="rounded-full border border-white/40 p-2 lg:hidden"
            onClick={() => setOpen((current) => !current)}
            aria-label="Toggle navigation"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="size-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={open ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'}
              />
            </svg>
          </button>
        </div>
        {open && (
          <nav className="border-t border-white/20 bg-[#551567] p-4 lg:hidden">
            <ul className="space-y-2 text-center text-sm font-extrabold uppercase tracking-[0.16em]">
              {navItems.map((item) => (
                <li key={item.to}>
                  <Link
                    onClick={() => setOpen(false)}
                    className={`block rounded-full px-4 py-3 ${isActive(item.to) ? 'bg-white text-[#6f2182]' : 'text-white hover:bg-white/15'}`}
                    to={item.to}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </header>
      <div className="bg-[#f2b800] px-4 py-2 text-center text-xs font-extrabold uppercase tracking-[0.18em] text-[#431052]">
        {campaign.paidFor}
      </div>
    </>
  );
}

function HomePage() {
  return (
    <main>
      <section className="relative isolate overflow-hidden bg-[#6f2182] text-white">
        <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(245,196,0,0.22)_0,rgba(245,196,0,0.22)_18%,transparent_18%,transparent_100%)]" />
        <PalmStars className="absolute right-5 top-8 hidden w-48 opacity-90 lg:block" />
        <div className="relative mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-[1.08fr_0.92fr] lg:px-8 lg:py-24">
          <div className="flex flex-col justify-center">
            <p className="mb-5 inline-flex w-fit rounded-full bg-[#f5c400] px-5 py-2 text-sm font-black uppercase tracking-[0.18em] text-[#6f2182] shadow-xl">
              Re-elect Beau Rawlins
            </p>
            <h1
              className="text-5xl font-black leading-tight sm:text-6xl lg:text-7xl"
              style={{ fontFamily: 'Source Serif 4, serif' }}
            >
              Lower taxes. Safer streets. Better roads.
            </h1>
            <p className="text-white/82 mt-7 max-w-2xl text-lg leading-8">
              Beau Rawlins is running to continue serving Galveston City Council District 5 with
              fiscally responsible leadership, strong support for first responders, and a practical
              plan to improve roads, drainage, and core city services.
            </p>
            <div className="mt-9 flex flex-col gap-4 sm:flex-row">
              <Link
                to="/election-information"
                className="rounded-full bg-[#f2b800] px-8 py-4 text-center text-sm font-black uppercase tracking-[0.16em] text-[#431052] shadow-xl transition hover:bg-white"
              >
                Make a voting plan
              </Link>
              <Link
                to="/contact"
                className="rounded-full border-2 border-white bg-white px-8 py-4 text-center text-sm font-black uppercase tracking-[0.16em] text-[#6f2182] shadow-xl transition hover:bg-[#f5c400]"
              >
                Join the team
              </Link>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -right-5 -top-5 size-full rounded-[2rem] bg-[#f2b800]" />
            <img
              src={assets.headshot}
              alt="Beau Rawlins"
              className="relative z-10 w-full rounded-[2rem] border-8 border-white object-cover shadow-2xl"
            />
            <div className="relative z-20 mx-6 -mt-10 rounded-2xl bg-white p-5 text-center text-[#281331] shadow-2xl">
              <p className="text-sm font-black uppercase tracking-[0.18em] text-[#6f2182]">
                {campaign.electionDayName}
              </p>
              <p className="mt-1 text-2xl font-black">{campaign.electionDay}</p>
              <p className="mt-1 text-sm font-bold text-[#66546f]">
                Polls open {campaign.electionDayHours}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-5 md:grid-cols-4">
            {proofPoints.map((point) => (
              <div key={point} className="rounded-2xl bg-white p-6 shadow-lg ring-1 ring-black/5">
                <p className="text-sm font-black uppercase tracking-[0.16em] text-[#8f6400]">
                  District 5
                </p>
                <p className="mt-3 text-xl font-black leading-7 text-[#6f2182]">{point}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <div>
            <p className="font-black uppercase tracking-[0.2em] text-[#8f6400]">Why Beau</p>
            <h2
              className="mt-3 text-4xl font-black text-[#6f2182]"
              style={{ fontFamily: 'Source Serif 4, serif' }}
            >
              Beau is focused on the issues Galveston residents live with every day.
            </h2>
            <p className="mt-5 text-lg leading-8 text-[#66546f]">
              From city spending to public safety to roads and drainage, Beau is working to keep
              Galveston focused on the fundamentals: responsible budgets, reliable services, and
              real infrastructure results for District 5 families and businesses.
            </p>
          </div>
          <div className="grid gap-5 md:grid-cols-3 lg:grid-cols-1">
            {priorities.map((priority) => (
              <div key={priority.title} className="rounded-[1.5rem] bg-[#f8f1fb] p-7 shadow-lg">
                <h3 className="text-2xl font-black text-[#6f2182]">{priority.title}</h3>
                <p className="mt-3 leading-7 text-[#66546f]">{priority.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[1fr_0.95fr]">
          <SignupPanel />
          <div className="rounded-[2rem] bg-[#6f2182] p-8 text-white shadow-xl">
            <div className="flex items-center gap-4">
              <PalmStars className="h-20 w-28 shrink-0" />
              <p className="font-black uppercase tracking-[0.2em] text-[#f5c400]">
                Beau for District 5
              </p>
            </div>
            <h2
              className="mt-3 text-4xl font-black"
              style={{ fontFamily: 'Source Serif 4, serif' }}
            >
              Re-elect Beau Rawlins.
            </h2>
            <p className="text-white/78 mt-5 leading-7">
              The runoff is here. Help Beau keep the focus on lower taxes, safer streets, better
              roads, and responsive District 5 service.
            </p>
            <div className="mt-7 grid gap-4 sm:grid-cols-2">
              <img
                src={assets.logo}
                alt="Re-elect Beau Rawlins logo"
                className="rounded-2xl bg-white object-cover shadow-lg"
              />
              <img
                src={assets.adSecondary}
                alt="Beau Rawlins campaign support graphic"
                className="rounded-2xl bg-white object-cover shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#fbf7fd] px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-8 rounded-[2rem] bg-white p-8 shadow-xl ring-1 ring-black/5 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
          <img
            src={assets.communityOne}
            alt="Galveston community context"
            className="w-full rounded-3xl object-cover shadow-lg"
          />
          <div>
            <p className="font-black uppercase tracking-[0.2em] text-[#8f6400]">Stay connected</p>
            <h2
              className="mt-3 text-4xl font-black text-[#6f2182]"
              style={{ fontFamily: 'Source Serif 4, serif' }}
            >
              District 5 should never have to guess who is listening.
            </h2>
            <p className="mt-5 text-lg leading-8 text-[#66546f]">
              Use the contact page for questions, concerns, sign requests, neighborhood events, or
              volunteer help. If you are looking for official city service, we also link Beau&apos;s
              public city profile and Galveston voting resources.
            </p>
            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <Link
                to="/contact"
                className="rounded-full bg-[#6f2182] px-8 py-4 text-center text-sm font-black uppercase tracking-[0.16em] text-white shadow-lg transition hover:bg-[#551567]"
              >
                Contact campaign
              </Link>
              <a
                href={campaign.galvestonVotesUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full border-2 border-[#6f2182] px-8 py-4 text-center text-sm font-black uppercase tracking-[0.16em] text-[#6f2182] transition hover:bg-[#f5c400]"
              >
                Voting info
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

type SignupForm = {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
};

const initialSignupForm: SignupForm = {
  email: '',
  firstName: '',
  lastName: '',
  phone: '',
};

function SignupPanel() {
  const [form, setForm] = useState<SignupForm>(initialSignupForm);
  const [phoneConsent, setPhoneConsent] = useState(false);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setForm((current) => {
      switch (name) {
        case 'email': {
          return { ...current, email: value };
        }
        case 'firstName': {
          return { ...current, firstName: value };
        }
        case 'lastName': {
          return { ...current, lastName: value };
        }
        case 'phone': {
          return { ...current, phone: value };
        }
        default: {
          return current;
        }
      }
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus('submitting');

    try {
      const data = new URLSearchParams();
      for (const [key, value] of Object.entries(form)) {
        data.append(key, value);
      }
      data.append('campaign', campaign.committee);
      data.append('source', 'homepage-signup');
      data.append('phoneConsent', phoneConsent.toString());

      const response = await fetch(campaign.contactEndpoint, {
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
      });

      if (!response.ok) {
        throw new Error('Signup failed.');
      }

      setForm(initialSignupForm);
      setPhoneConsent(false);
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="rounded-[2rem] bg-white p-8 shadow-xl ring-1 ring-black/5">
      <p className="font-black uppercase tracking-[0.2em] text-[#8f6400]">Join the campaign</p>
      <h2
        className="mt-3 text-4xl font-black text-[#6f2182]"
        style={{ fontFamily: 'Source Serif 4, serif' }}
      >
        Help keep District 5 moving.
      </h2>
      <form className="mt-8 grid gap-4" onSubmit={handleSubmit}>
        <input
          name="email"
          type="email"
          required
          value={form.email}
          onChange={handleChange}
          placeholder="Email address"
          className="rounded-xl border border-[#dbc4e4] bg-[#fffaf0] p-4 outline-none ring-[#f2b800] transition focus:ring-2"
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <input
            name="firstName"
            value={form.firstName}
            onChange={handleChange}
            placeholder="First name"
            className="rounded-xl border border-[#dbc4e4] bg-[#fffaf0] p-4 outline-none ring-[#f2b800] transition focus:ring-2"
          />
          <input
            name="lastName"
            value={form.lastName}
            onChange={handleChange}
            placeholder="Last name"
            className="rounded-xl border border-[#dbc4e4] bg-[#fffaf0] p-4 outline-none ring-[#f2b800] transition focus:ring-2"
          />
        </div>
        <input
          name="phone"
          type="tel"
          value={form.phone}
          onChange={handleChange}
          placeholder="Phone (optional for SMS updates)"
          className="rounded-xl border border-[#dbc4e4] bg-[#fffaf0] p-4 outline-none ring-[#f2b800] transition focus:ring-2"
        />
        <div className="flex gap-3 rounded-2xl bg-[#f8f1fb] p-4 text-sm leading-6 text-[#66546f]">
          <input
            id="homepage-phone-consent"
            aria-label="Optional SMS opt-in consent"
            type="checkbox"
            checked={phoneConsent}
            onChange={() => setPhoneConsent((current) => !current)}
            className="mt-1 size-5 rounded border-[#6f2182] text-[#6f2182]"
          />
          <SmsConsentDisclosure />
        </div>
        <button
          type="submit"
          disabled={status === 'submitting' || !form.email.trim()}
          className={`rounded-full px-8 py-4 text-sm font-black uppercase tracking-[0.16em] shadow-lg transition ${status === 'submitting' || !form.email.trim() ? 'bg-gray-300 text-gray-500' : 'bg-[#f2b800] text-[#431052] hover:bg-white'}`}
        >
          {status === 'submitting' ? 'Submitting...' : 'Sign up'}
        </button>
        {status === 'success' && (
          <p className="font-semibold text-green-700">
            Thanks. You are connected with the campaign.
          </p>
        )}
        {status === 'error' && (
          <p className="font-semibold text-[#8f6400]">
            Something went wrong. Please email {campaign.email}.
          </p>
        )}
      </form>
    </div>
  );
}

function Footer() {
  return (
    <footer className="border-t-4 border-[#f2b800] bg-[#6f2182] px-4 py-12 text-white sm:px-6 lg:px-8">
      <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[1.2fr_0.8fr]">
        <div>
          <img
            src={assets.logo}
            alt="Re-elect Beau Rawlins logo"
            className="mb-6 h-32 rounded-xl border-4 border-[#f5c400] bg-white object-contain"
          />
          <p className="text-sm font-black uppercase tracking-[0.2em] text-[#f5c400]">
            {campaign.committee}
          </p>
          <p className="mt-4 max-w-2xl text-white/75">{campaign.paidFor}</p>
          <p className="mt-2 text-sm text-white/60">
            Official runoff reference: {researchNotes.runoff}
          </p>
          <div className="mt-6 flex flex-wrap gap-4 text-sm font-semibold text-white/80">
            <Link to="/privacy-policy" className="hover:text-[#f5c400]">
              Privacy Policy
            </Link>
            <Link to="/terms-and-conditions" className="hover:text-[#f5c400]">
              Terms & Conditions
            </Link>
            <Link to="/contact" className="hover:text-[#f5c400]">
              Contact
            </Link>
          </div>
        </div>
        <div className="lg:text-right">
          <a
            href={`mailto:${campaign.email}`}
            className="block text-lg font-bold text-white hover:text-[#f5c400]"
          >
            {campaign.email}
          </a>
          <a
            href={campaign.facebookUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-block font-bold text-white/80 hover:text-[#f5c400]"
          >
            Facebook campaign page
          </a>
          <p className="mt-6 text-sm text-white/60">&copy; 2026 {campaign.committee}.</p>
        </div>
      </div>
    </footer>
  );
}

export default App;
